import {FC, useEffect, useRef, useState} from 'react'
import {api, HostApplication} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.aiiplan.com/api').replace('/api', '')

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

function resolvePhoto(photo?: string): string | null {
  if (!photo) return null
  return photo.startsWith('http') || photo.startsWith('data:') ? photo : `${API_BASE}${photo}`
}

const AVATAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e']
function avatarBg(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

const AppAvatar: FC<{name: string; photo?: string; size?: number}> = ({name, photo, size = 44}) => {
  const [failed, setFailed] = useState(false)
  const url = !failed ? resolvePhoto(photo) : null
  if (url) {
    return <img src={url} alt={name} onError={() => setFailed(true)}
      style={{width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}} />
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: avatarBg(name),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.35,
    }}>
      {(name[0] ?? '?').toUpperCase()}
    </div>
  )
}

function statusStyle(s: HostApplication['status']) {
  if (s === 'approved') return {bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0'}
  if (s === 'rejected') return {bg: '#fee2e2', color: '#dc2626', border: '#fca5a5'}
  return {bg: '#fef9c3', color: '#a16207', border: '#fde68a'}
}

// ── Action Menu ───────────────────────────────────────────────────────────────
const AppActionMenu: FC<{app: HostApplication; onView: () => void; onApprove: () => void; onReject: () => void}> = ({app, onView, onApprove, onReject}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const items = [
    {label: 'View Details', action: onView, icon: <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/></svg>},
    ...(app.status === 'pending' ? [
      {label: 'Approve', action: onApprove, icon: <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='20 6 9 17 4 12'/></svg>},
      {label: 'Reject', action: onReject, icon: <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><circle cx='12' cy='12' r='10'/><line x1='15' y1='9' x2='9' y2='15'/><line x1='9' y1='9' x2='15' y2='15'/></svg>, danger: true},
    ] : []),
  ]

  return (
    <div ref={ref} style={{position: 'relative', display: 'inline-block'}}>
      <button onClick={() => setOpen(o => !o)}
        style={{background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: '#94a3b8', fontSize: 20, lineHeight: 1}}>⋮</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 200, minWidth: 150, overflow: 'hidden', marginTop: 4,
        }}>
          {items.map(item => (
            <button key={item.label}
              onClick={() => { item.action(); setOpen(false) }}
              style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: (item as any).danger ? '#ef4444' : '#374151'}}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              {item.icon}{item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────
const AppDetailDrawer: FC<{
  app: HostApplication
  categoryLabel: string
  onClose: () => void
  onApprove: (id: string) => Promise<void>
  onReject: (id: string, reason: string) => Promise<void>
  onVerify: (id: string, flags: Partial<HostApplication>) => Promise<void>
}> = ({app, categoryLabel, onClose, onApprove, onReject, onVerify}) => {
  const [loading, setLoading]   = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason]     = useState('')
  const [error, setError]       = useState('')
  const [verifyingKey, setVerifyingKey] = useState<string | null>(null)
  const ss = statusStyle(app.status)

  const VerifyToggle: FC<{label: string; field: keyof HostApplication}> = ({label, field}) => {
    const on = !!app[field]
    return (
      <button
        disabled={verifyingKey === field}
        onClick={async () => { setVerifyingKey(field); try { await onVerify(app.id, {[field]: !on}) } finally { setVerifyingKey(null) } }}
        style={{
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
          border: `1px solid ${on ? '#bbf7d0' : '#e2e8f0'}`,
          background: on ? '#f0fdf4' : '#fff', color: on ? '#16a34a' : '#64748b',
        }}>
        {verifyingKey === field ? '…' : on ? '✓ Verified' : `Verify ${label}`}
      </button>
    )
  }

  const handleApprove = async () => {
    setLoading(true); setError('')
    try { await onApprove(app.id) } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const handleReject = async () => {
    setLoading(true); setError('')
    try { await onReject(app.id, reason) } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const Section: FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <div style={{marginBottom: 24}}>
      <div style={{fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f1f5f9'}}>
        {title}
      </div>
      {children}
    </div>
  )

  const Field: FC<{label: string; value?: string | number}> = ({label, value}) => (
    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f8fafc'}}>
      <span style={{fontSize: 13, color: '#64748b', minWidth: 120}}>{label}</span>
      <span style={{fontSize: 13, fontWeight: 500, color: '#0f172a', textAlign: 'right', maxWidth: '60%'}}>{value || '—'}</span>
    </div>
  )

  return (
    <>
      <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200}} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100vh', width: 480,
        background: '#fff', zIndex: 201, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', fontFamily: 'inherit',
      }}>
        {/* Header */}
        <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0}}>
          <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Application Details</h3>
          <button onClick={onClose} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{flex: 1, overflowY: 'auto', padding: '24px'}}>
          {error && (
            <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16}}>{error}</div>
          )}

          {/* Status bar */}
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 20}}>
            <div>
              <div style={{fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4}}>Status</div>
              <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                {app.status}
              </span>
            </div>
            <div style={{fontSize: 12, color: '#94a3b8', textAlign: 'right'}}>
              Submitted<br />
              <span style={{fontWeight: 500, color: '#374151'}}>{new Date(app.createdAt).toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}</span>
            </div>
          </div>

          {/* Avatar + identity */}
          <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: '16px', background: '#f8fafc', borderRadius: 12}}>
            <AppAvatar name={app.fullName} photo={app.hostPhoto} size={64} />
            <div>
              <div style={{fontSize: 18, fontWeight: 700, color: '#0f172a'}}>{app.fullName}</div>
              <div style={{fontSize: 13, color: '#64748b', marginTop: 2}}>{app.email}</div>
              {categoryLabel && (
                <span style={{display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#ede9fe', color: '#7c3aed'}}>
                  {categoryLabel}
                </span>
              )}
            </div>
          </div>

          <Section title='Personal Information'>
            <Field label='Full Name' value={app.fullName} />
            <Field label='Email' value={app.email} />
            <Field label='Phone' value={app.phone} />
          </Section>

          <Section title='Business Information'>
            <Field label='Business Name' value={app.businessName} />
            <Field label='Business Type' value={categoryLabel || app.businessType} />
            <Field label='Address' value={app.address} />
            <Field label='City' value={app.city} />
            <Field label='State/Region' value={app.stateRegion} />
            <Field label='Zip Code' value={app.zipCode} />
          </Section>

          <Section title='Service Details'>
            <Field label='Experience' value={app.yearsExperience ? `${app.yearsExperience} years` : undefined} />
            {app.packages && app.packages.length > 0 ? (
              <div style={{marginTop: 10}}>
                <div style={{fontSize: 12, color: '#64748b', marginBottom: 6}}>Packages</div>
                {app.packages.map((pkg, i) => (
                  <div key={i} style={{fontSize: 13, color: '#374151', background: '#f8fafc', padding: '8px 12px', borderRadius: 8, marginBottom: 6}}>
                    <strong>{pkg.name || `Package ${i + 1}`}</strong> — {pkg.pricingModel}{pkg.baseRate ? ` · $${pkg.baseRate}` : ''}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <Field label='Pricing Model' value={app.pricingModel} />
                <Field label='Base Rate' value={app.baseRate ? `$${app.baseRate}` : undefined} />
              </>
            )}
            <Field label='Min. Booking' value={app.minBooking} />
            {app.serviceDescription && (
              <div style={{marginTop: 10}}>
                <div style={{fontSize: 12, color: '#64748b', marginBottom: 6}}>Description</div>
                <div style={{fontSize: 13, color: '#374151', background: '#f8fafc', padding: '10px 12px', borderRadius: 8, lineHeight: 1.6}}>{app.serviceDescription}</div>
              </div>
            )}
          </Section>

          {app.availability?.length > 0 && (
            <Section title='Availability'>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                {app.availability.map(a => (
                  <span key={a} style={{padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#eff6ff', color: '#3b82f6'}}>
                    {a}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {app.portfolioPhotos && app.portfolioPhotos.length > 0 && (
            <Section title='Portfolio'>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
                {app.portfolioPhotos.map((url, i) => {
                  const src = url.startsWith('http') ? url : `${API_BASE}${url}`
                  return (
                    <a key={i} href={src} target='_blank' rel='noreferrer'>
                      <img src={src} alt={`Portfolio ${i + 1}`}
                        style={{width: 100, height: 100, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0'}} />
                    </a>
                  )
                })}
              </div>
            </Section>
          )}

          <Section title='Documents & Verification'>
            {([
              {label: 'Business License',           applicable: app.licenseApplicable,       url: app.businessLicenseUrl,    field: 'verifiedBusinessLicense' as const},
              {label: 'Liability Insurance',        applicable: app.insuranceApplicable,     url: app.liabilityInsuranceUrl, field: 'verifiedInsurance' as const},
              {label: 'Professional Certification', applicable: app.certificationApplicable, url: app.professionalCertUrl,    field: 'verifiedCertification' as const},
            ]).map(d => {
              const src = d.url ? (d.url.startsWith('http') ? d.url : `${API_BASE}${d.url}`) : null
              return (
                <div key={d.label} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
                  <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                    <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>{d.label}</span>
                    {src ? (
                      <a href={src} target='_blank' rel='noreferrer' style={{fontSize: 12, fontWeight: 600, color: '#3b82f6'}}>View document</a>
                    ) : d.applicable === false ? (
                      <span style={{fontSize: 12, color: '#94a3b8'}}>Not applicable</span>
                    ) : (
                      <span style={{fontSize: 12, color: '#f59e0b', fontWeight: 600}}>Pending upload</span>
                    )}
                  </div>
                  {/* Only allow verifying once the document is actually uploaded. */}
                  {src && <VerifyToggle label={d.label.split(' ')[0]} field={d.field} />}
                </div>
              )
            })}

            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>Government-issued ID</span>
                <div style={{display: 'flex', gap: 12}}>
                  {([{label: 'Front', url: app.govIdFrontUrl}, {label: 'Back', url: app.govIdBackUrl}]).map(s => {
                    const src = s.url ? (s.url.startsWith('http') ? s.url : `${API_BASE}${s.url}`) : null
                    return src
                      ? <a key={s.label} href={src} target='_blank' rel='noreferrer' style={{fontSize: 12, fontWeight: 600, color: '#3b82f6'}}>{s.label}</a>
                      : <span key={s.label} style={{fontSize: 12, color: '#ef4444'}}>{s.label}: missing</span>
                  })}
                </div>
              </div>
              {app.govIdFrontUrl && app.govIdBackUrl && <VerifyToggle label='ID' field='verifiedGovId' />}
            </div>

            <Field label='Legal Name' value={app.legalName || undefined} />
            <Field label='Date of Birth' value={app.dateOfBirth || undefined} />
            <Field label='SSN' value={app.ssnLast4 ? `•••–••–${app.ssnLast4}` : undefined} />
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 0'}}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>Background Check</span>
                <span style={{fontSize: 12, color: app.backgroundConsent ? '#16a34a' : '#94a3b8'}}>
                  Consent: {app.backgroundConsent ? 'Authorized' : 'Not authorized'}
                </span>
              </div>
              {app.backgroundConsent && <VerifyToggle label='Check' field='verifiedBackgroundCheck' />}
            </div>
          </Section>

          {app.status === 'rejected' && app.rejectionReason && (
            <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16}}>
              <div style={{fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 4}}>Rejection Reason</div>
              <div style={{fontSize: 13, color: '#374151'}}>{app.rejectionReason}</div>
            </div>
          )}

          {app.status === 'pending' && rejecting && (
            <div style={{marginTop: 8}}>
              <label style={{fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6}}>
                Rejection Reason <span style={{color: '#ef4444'}}>*</span>
              </label>
              <textarea
                rows={3}
                placeholder='Explain why this application is being rejected...'
                value={reason}
                onChange={e => setReason(e.target.value)}
                style={{width: '100%', padding: '10px 12px', border: `1px solid ${!reason.trim() ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'}}
              />
              {!reason.trim() && <div style={{fontSize: 12, color: '#ef4444', marginTop: 4}}>Rejection reason is required.</div>}
            </div>
          )}
        </div>

        {/* Footer */}
        {app.status === 'pending' && (
          <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0}}>
            {rejecting ? (
              <>
                <button onClick={() => setRejecting(false)} disabled={loading}
                  style={{padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                  Cancel
                </button>
                <button onClick={handleReject} disabled={loading || !reason.trim()}
                  style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600, opacity: !reason.trim() ? 0.5 : 1}}>
                  {loading ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setRejecting(true)} disabled={loading}
                  style={{padding: '9px 18px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: 14, cursor: 'pointer', color: '#dc2626', fontWeight: 500}}>
                  Reject
                </button>
                <button onClick={handleApprove} disabled={loading}
                  style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#22c55e', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600}}>
                  {loading ? 'Approving...' : 'Approve & Assign Host Role'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const HostApplicationsPage: FC = () => {
  const [categoryMap, setCategoryMap]   = useState<Record<string, string>>({})
  const [filter, setFilter]             = useState<StatusFilter>('all')
  const [selected, setSelected]         = useState<HostApplication | null>(null)

  const {items: apps, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated(
      (p, l, s) => api.getHostApplicationsPaged(p, l, s, {status: filter !== 'all' ? filter : undefined}),
      {limit: 20, deps: [filter]},
    )

  useEffect(() => {
    api.getServiceCategories()
      .then(cats => {
        const map: Record<string, string> = {}
        cats.forEach(c => { map[c.id] = c.label })
        setCategoryMap(map)
      })
      .catch(() => {})
  }, [])

  const handleApprove = async (id: string) => {
    try {
      await api.approveHostApplication(id)
      setSelected(prev => prev?.id === id ? {...prev, status: 'approved'} : prev)
      reload()
      toast('Application approved')
    } catch (e: any) {
      toast(e?.message ?? 'Approve failed', 'error')
      throw e
    }
  }

  const handleReject = async (id: string, reason: string) => {
    try {
      await api.rejectHostApplication(id, reason)
      setSelected(prev => prev?.id === id ? {...prev, status: 'rejected', rejectionReason: reason} : prev)
      reload()
      toast('Application rejected')
    } catch (e: any) {
      toast(e?.message ?? 'Reject failed', 'error')
      throw e
    }
  }

  const handleVerify = async (id: string, flags: Partial<HostApplication>) => {
    try {
      const updated = await api.updateHostVerification(id, flags)
      setSelected(prev => prev?.id === id ? {...prev, ...updated} : prev)
      reload()
      toast('Verification updated')
    } catch (e: any) {
      toast(e?.message ?? 'Verification update failed', 'error')
      throw e
    }
  }

  const counts = {
    all:      total,
    pending:  apps.filter(a => a.status === 'pending').length,
    approved: apps.filter(a => a.status === 'approved').length,
    rejected: apps.filter(a => a.status === 'rejected').length,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Account Requests</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Review and manage host account applications</p>
      </div>

      {/* Stat cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20}}>
        {([
          {key: 'all',      label: 'Total',    bg: '#eff6ff', color: '#3b82f6'},
          {key: 'pending',  label: 'Pending',  bg: '#fefce8', color: '#ca8a04'},
          {key: 'approved', label: 'Approved', bg: '#f0fdf4', color: '#22c55e'},
          {key: 'rejected', label: 'Rejected', bg: '#fef2f2', color: '#ef4444'},
        ] as {key: StatusFilter; label: string; bg: string; color: string}[]).map(s => (
          <div key={s.key}
            onClick={() => setFilter(s.key)}
            style={{
              background: '#fff', borderRadius: 12, padding: '16px 20px',
              border: filter === s.key ? `2px solid ${s.color}` : '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
          >
            <div style={{fontSize: 22, fontWeight: 700, color: s.color}}>{counts[s.key]}</div>
            <div style={{fontSize: 13, color: '#94a3b8', marginTop: 4}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: '1 1 260px'}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input
            type='text' placeholder='Search by name, email, business...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box'}}
          />
        </div>

        <select value={filter} onChange={e => setFilter(e.target.value as StatusFilter)}
          style={{flex: '0 0 160px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Status</option>
          <option value='pending'>Pending</option>
          <option value='approved'>Approved</option>
          <option value='rejected'>Rejected</option>
        </select>

        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['APPLICANT', 'BUSINESS', 'LOCATION', 'STATUS', 'SUBMITTED', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.map((a, i) => {
                  const ss = statusStyle(a.status)
                  const catLabel = categoryMap[a.businessType] || a.businessType
                  return (
                    <tr key={a.id}
                      style={{borderBottom: i < apps.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                    >
                      {/* Applicant */}
                      <td style={{padding: '14px 20px', minWidth: 220}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                          <AppAvatar name={a.fullName} photo={a.hostPhoto} size={44} />
                          <div>
                            <div style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{a.fullName}</div>
                            <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{a.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Business */}
                      <td style={{padding: '14px 20px', minWidth: 180}}>
                        <div style={{fontWeight: 600, color: '#0f172a'}}>{a.businessName}</div>
                        <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{catLabel}</div>
                      </td>

                      {/* Location */}
                      <td style={{padding: '14px 20px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {a.city}{a.stateRegion ? `, ${a.stateRegion}` : ''}
                      </td>

                      {/* Status */}
                      <td style={{padding: '14px 20px'}}>
                        <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                          {a.status}
                        </span>
                      </td>

                      {/* Submitted */}
                      <td style={{padding: '14px 20px', color: '#374151', whiteSpace: 'nowrap', fontSize: 13}}>
                        {new Date(a.createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
                      </td>

                      {/* Actions */}
                      <td style={{padding: '14px 20px', textAlign: 'right'}}>
                        <AppActionMenu
                          app={a}
                          onView={() => setSelected(a)}
                          onApprove={async () => { if (window.confirm(`Approve ${a.fullName}'s application?`)) await handleApprove(a.id) }}
                          onReject={() => setSelected(a)}
                        />
                      </td>
                    </tr>
                  )
                })}
                {apps.length === 0 && (
                  <tr><td colSpan={6} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No applications found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {apps.length} of {total} applications</span>
            <div style={{display: 'flex', gap: 8}}>
              {[
                {label: 'Pending', count: counts.pending, bg: '#fef9c3', color: '#a16207'},
                {label: 'Approved', count: counts.approved, bg: '#dcfce7', color: '#16a34a'},
                {label: 'Rejected', count: counts.rejected, bg: '#fee2e2', color: '#dc2626'},
              ].map(s => (
                <span key={s.label} style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color}}>
                  {s.label}: {s.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      {selected && (
        <AppDetailDrawer
          app={selected}
          categoryLabel={categoryMap[selected.businessType] || ''}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onVerify={handleVerify}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const HostApplicationsPageWrapper: FC = () => <HostApplicationsPage />

export {HostApplicationsPageWrapper}
